"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect } from "react";
import { useRouter as useNextRouter, useSearchParams } from "next/navigation";
import { useGameLocale, useT } from "@/lib/i18n";
import { usePathname, useRouter } from "@/i18n/navigation";
import { routing, type Locale } from "@/i18n/routing";
import { LANG_NAMES } from "@/lib/languages";
const CODE_TO_SHORT: Record<string, string> = {
  deu: "DE",
  eng: "EN",
  esp: "ES",
  fra: "FR",
  ita: "IT",
  jpn: "JP",
  kor: "KR",
  pol: "PL",
  ptb: "PT",
  rus: "RU",
  spa: "LA",
  tha: "TH",
  tur: "TR",
  zhs: "CN",
  zht: "TW"
};
const LANGUAGES: {
  code: Locale;
  name: string;
}[] = routing.locales.map(code => ({
  code,
  name: code === "eng" ? "English" : LANG_NAMES[code]
})).sort((a, b) => a.code.localeCompare(b.code));
export default function LanguageSelector(t0) {
  const $ = _c(30);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    align: t2
  } = t1;
  const align = t2 === undefined ? "right" : t2;
  const lang = useGameLocale();
  const t = useT();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const router = useRouter();
  const nextRouter = useNextRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t3;
  let t4;
  if ($[2] !== open) {
    t3 = () => {
      const handleClick = function handleClick(e) {
        if (open && menuRef.current && !menuRef.current.contains(e.target as Node) && buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
          setOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClick);
      return () => document.removeEventListener("mousedown", handleClick);
    };
    t4 = [open];
    $[2] = open;
    $[3] = t3;
    $[4] = t4;
  } else {
    t3 = $[3];
    t4 = $[4];
  }
  useEffect(t3, t4);
  let t5;
  if ($[5] !== nextRouter || $[6] !== pathname || $[7] !== router || $[8] !== searchParams) {
    t5 = function handleLangChange(locale) {
      setOpen(false);
      const query = searchParams.toString();
      const href = query ? `${pathname}?${query}` : pathname;
      if (locale === routing.defaultLocale) {
        nextRouter.replace(href);
      } else {
        router.replace(href, {
          locale
        });
      }
    };
    $[5] = nextRouter;
    $[6] = pathname;
    $[7] = router;
    $[8] = searchParams;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleLangChange = t5;
  let t6;
  if ($[10] !== open) {
    t6 = () => setOpen(!open);
    $[10] = open;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = CODE_TO_SHORT[lang] || "EN";
  let t8;
  if ($[12] !== t || $[13] !== t7) {
    t8 = t("Select language (current: {code})", {
      code: t7
    });
    $[12] = t;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = CODE_TO_SHORT[lang] || "EN";
  let t11;
  if ($[16] !== t10) {
    t11 = <span className="hidden sm:inline text-xs font-medium">{t10}</span>;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t11 || $[19] !== t6 || $[20] !== t8) {
    t12 = <button ref={buttonRef} onClick={t6} className="inline-flex items-center gap-0 sm:gap-1.5 h-9 w-9 sm:w-auto sm:px-3 justify-center rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] text-sm text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--border-accent)] transition-colors" aria-label={t8}>{t9}{t11}</button>;
    $[18] = t11;
    $[19] = t6;
    $[20] = t8;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== align || $[23] !== handleLangChange || $[24] !== lang || $[25] !== open) {
    t13 = open && <div ref={menuRef} className={`absolute ${align === "left" ? "left-0" : "right-0"} top-full mt-2 w-48 max-h-80 overflow-y-auto rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] shadow-xl shadow-scrim/30 z-50`}><div className="py-1">{LANGUAGES.map(l => <button key={l.code} onClick={() => handleLangChange(l.code)} className={`w-full text-left px-4 py-2 text-sm transition-colors ${l.code === lang ? "text-[var(--accent-gold)] bg-[var(--bg-card)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card)]"}`}><span className="font-medium">{l.name}</span><span className="text-xs text-[var(--text-muted)] ml-2">{CODE_TO_SHORT[l.code]}</span></button>)}</div></div>;
    $[22] = align;
    $[23] = handleLangChange;
    $[24] = lang;
    $[25] = open;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== t12 || $[28] !== t13) {
    t14 = <div className="relative">{t12}{t13}</div>;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  return t14;
}